import React, { useState, useEffect } from "react";

// internal imports
import { login } from "../../../services/api";
import colors from "../../../theme";
import SnackbarComp from "../../Snackbar/SnackbarComp";
import RSP_LOGO_RG from "../../../assets/RSP_LOGO_RG.png";
import CopyRight from "../../copyright/CopyRight";
import version from "../../../../package.json";
import "./login.css";

// external imports
import useSignIn from "react-auth-kit/hooks/useSignIn";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import Cookies from "js-cookie";
import { formControlInputStyle } from "../../../styles/formControlStyles";

export default function SignIn() {
  const fcmToken = "";

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fcmToken: fcmToken,
  });

  const [errors, setErrors] = useState({});
  const [error, setError] = useState(null);
  const [statusCode, setStatusCode] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(true);

  const navigate = useNavigate();
  const signIn = useSignIn();

  useEffect(() => {
    if (fcmToken) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        fcmToken: fcmToken,
      }));
    }
  }, [fcmToken]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const validateForm = () => {
    const newErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const passwordPattern = /^(?=.*[A-Z])(?=.*\d)(?=.*[~!@#$%^&*()_\-+=\[\]{}\\/|<>?])[A-Za-z\d~!@#$%^&*()_\-+=\[\]{}\\/|<>?]{8,}$/; // eslint-disable-line

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!emailPattern.test(formData.email)) {
      newErrors.email = "Invalid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (!passwordPattern.test(formData.password)) {
      newErrors.password =
        "Password must be at least 8 characters long, contain at least one digit, one special character, and one uppercase letter";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    if (validateForm()) {
      try {
        await login(
          formData,
          navigate,
          signIn,
          setError,
          setOpenSnackbar,
          setStatusCode
        );
        Cookies.set("fcmToken", fcmToken);
      } catch (error) {
        if (error.response && error.response.data) {
          setError(error.response.data.message || "An unknown error occurred");
          setStatusCode(
            error.response.data.statusCode || error.response.data.status || 500
          );
        } else {
          setError("An unknown error occurred");
          setStatusCode(500);
        }
        setOpenSnackbar(true);
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };
  const handleLoginWithGoogle = async (event) => {
    event.preventDefault();
    try {
      window.location.href = `${process.env.REACT_APP_API_URL}/auth/google`;
    } catch (error) {
      console.error("Google login error:", error);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box sx={{ marginBottom: "10px" }}>
            <img src={RSP_LOGO_RG} alt="RSP Logo" width={300} height={50} />
          </Box>

          <Typography
            variant="caption"
            gutterBottom
            sx={{ fontWeight: "normal", textAlign: "center", marginBottom: "36px" }}
          >
            v{version.version} {process.env.REACT_APP_API_URL.includes("test") ? "(Test)" : ""}
          </Typography>

          <Box component="form" onSubmit={handleLogin} sx={{ mt: 1, width: '100%' }}>
            <TextField
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              InputProps={{
                style: {
                  borderRadius: 20,
                  height: "3.5rem",
                },
              }}
              sx={formControlInputStyle}
            />
            <div style={{ marginBottom: "20px" }}></div>
            <FormControl
              fullWidth
              variant="outlined"
              error={!!errors.password}
              sx={formControlInputStyle}
            >
              <InputLabel htmlFor="outlined-adornment-password">
                Password
              </InputLabel>
              <OutlinedInput
                fullWidth
                name="password"
                id="outlined-adornment-password"
                type={!showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Password"
                style={{ borderRadius: 20, height: "3.5rem" }}
              />
              {errors.password && (
                <div style={{ color: "red", fontSize: "0.8em" }}>
                  {errors.password}
                </div>
              )}
            </FormControl>

            <div className="container mt-1 mb-3" style={{ textAlign: "right" }}>
              <a
                href="/forgot-password"
                className="text-primary fw-bold d-block signup-col"
                style={{ textDecoration: "none", fontSize: "12px" }}
              >
                Forgot password?
              </a>
            </div>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                fontWeight: "bold",
                borderRadius: "20px",
                height: "3.5rem",
                backgroundColor: colors.primary,
                "&:hover": {
                  backgroundColor: colors.hoverButton,
                },
              }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : "Sign In"}
            </Button>

            <hr className="my-4"></hr>

            <Button
              fullWidth
              variant="contained"
              sx={{
                fontWeight: "bold",
                borderRadius: "20px",
                height: "3.5rem",
              }}
              data-mdb-button-init
              data-mdb-ripple-init
              className="btn btn-lg btn-block btn-primary btn-red custom-btn mb-3 border-rad-social-button"
              id="google-button"
              type="button"
              onClick={handleLoginWithGoogle}
            >
              <i className="fab fa-google me-2 text-purple-500 rounded "></i>
              Sign in/up with Google
            </Button>

            {/* <Button
              fullWidth
              variant="contained"
              sx={{
                fontWeight: "bold",
                borderRadius: "20px",
                height: "3.5rem",
              }}
              data-mdb-button-init
              data-mdb-ripple-init
              className="btn btn-lg btn-block btn-black border-rad-social-button"
              id="apple-button"
              type="button"
            >
              <i className="fab fa-apple me-2"></i>Sign in with Apple
            </Button> */}

            <div className="container mt-3">
              <div className="row">
                <div className="col">
                  <a
                    href="/sign-up"
                    className="text-primary fw-bold d-block text-center signup-col"
                    sx={{
                      marginTop: "1px",
                      color: "rgba(104, 85, 224, 1)",
                      fontWeight: "bold",
                      textAlign: "center",
                    }}
                  >
                    Don't have an account? Sign Up
                  </a>
                </div>
              </div>
            </div>
          </Box>
        </Box>
        <CopyRight sx={{ mt: 4, mb: 4 }} />
      </Container>
      <SnackbarComp
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        errorMessage={error}
        statusCode={statusCode}
      />
    </div>
  );
}
